/**
 * Power-ups that are vessels and ornaments (spec 02 §7.5): the Aladdin lamps, the potion flasks,
 * the lightning potion, the crystal balls on their stands and the bell. On the base at y = 0,
 * facing the camera (+z); colours from the `pu*` ramps.
 */

import { Vector3 } from 'three';
import { place } from '../../kit/MeshBuilder';
import type { Builder } from './charms';
import { arc, circlePath, lathe, rotX, rotZ, slab, Smooth, tube } from './shapes';

/** An oil lamp side on, spout forward (+x): a squat body in `ramp`, foot, lid, knob and handle in `trim`. */
export const lamp = (ramp: string, trim: string): Builder => (mb, p) => {
  const body = p.color(`${ramp}.1`);
  const t1 = p.color(`${trim}.1`);
  const t2 = p.color(`${trim}.2`);
  mb.cylinder(0.24, 0.16, 0.1, 16, t1, place(-0.04, 0, 0), t2);
  mb.cylinder(0.1, 0.13, 0.16, 12, t1, place(-0.04, 0.08, 0));
  mb.blob(0.46, 0.25, 0.32, body, place(-0.04, 0.42, 0), { topColor: p.color(`${ramp}.2`), detail: 3 });
  const s = new Smooth();
  const spout = [
    new Vector3(0.22, 0.42, 0), new Vector3(0.44, 0.46, 0), new Vector3(0.6, 0.53, 0),
    new Vector3(0.7, 0.62, 0), new Vector3(0.76, 0.7, 0),
  ];
  tube(s, spout, [0.17, 0.11, 0.075, 0.06, 0.055], 12, body);
  const handle: Vector3[] = [];
  for (let i = 0; i <= 10; i++) handle.push(arc(-0.52, 0.46, 0.15, 1.3 + (i / 10) * 3.7));
  tube(s, handle, 0.05, 8, t1);
  s.flush(mb);
  mb.cylinder(0.24, 0.24, 0.05, 16, t1, place(-0.06, 0.6, 0));
  mb.blob(0.21, 0.13, 0.2, t2, place(-0.06, 0.64, 0), { detail: 2 });
  mb.blob(0.07, 0.09, 0.07, t1, place(-0.06, 0.8, 0), { detail: 2 });
};

/** A round-bottomed flask, `ramp` liquid two-thirds up the glass, a cork in the neck. */
export const potion = (ramp: string): Builder => (mb, p) => {
  mb.blob(0.44, 0.44, 0.44, p.color(`${ramp}.1`), place(0, 0.46, 0), { topColor: p.color('puGlass.1'), detail: 3 });
  mb.cylinder(0.14, 0.13, 0.34, 14, p.color('puGlass.1'), place(0, 0.82, 0));
  const s = new Smooth();
  tube(s, circlePath(0, 0, 0.15, 16), 0.045, 8, p.color('puGlass.2'), { closed: true });
  s.flush(mb, rotX(0, 1.15, 0, Math.PI / 2));
  mb.cylinder(0.11, 0.14, 0.17, 12, p.color('puWood.1'), place(0, 1.12, 0), p.color('puWood.2'));
  // Bubbles rising in the liquid, and the glint on the glass.
  mb.blob(0.07, 0.07, 0.04, p.color(`${ramp}.2`), place(0.12, 0.32, 0.41), { detail: 1 });
  mb.blob(0.05, 0.05, 0.03, p.color(`${ramp}.2`), place(-0.02, 0.2, 0.42), { detail: 1 });
  mb.blob(0.06, 0.1, 0.04, p.color('puWhite.2'), place(-0.2, 0.64, 0.34), { detail: 1 });
};

/** The lightning potion: a conical flask of deep blue with a yellow bolt on its front. */
export const lightningPotion: Builder = (mb, p) => {
  const s = new Smooth();
  const profile: Array<[number, number]> = [
    [0, 0], [0.44, 0], [0.49, 0.05], [0.47, 0.14], [0.29, 0.56], [0.2, 0.76], [0.14, 0.82],
    [0.14, 1.02], [0.19, 1.04], [0.19, 1.1], [0, 1.1],
  ];
  const liquid = p.color('puBlue.1');
  const glass = p.color('puGlass.1');
  lathe(s, profile, 20, (seg) => (seg < 4 ? liquid : seg === 7 || seg === 8 ? p.color('puGlass.2') : glass));
  s.flush(mb);
  mb.cylinder(0.11, 0.13, 0.16, 12, p.color('puWood.1'), place(0, 1.08, 0), p.color('puWood.2'));
  // The bolt, leaning back with the flask's side.
  const bolt: Array<[number, number]> = [
    [-0.05, 1.0], [-0.25, 0.42], [0.0, 0.42], [-0.2, -0.15], [0.38, 0.58], [0.12, 0.58], [0.35, 1.0],
  ];
  const k = 0.6;
  const scaled = bolt.map(([x, y]): [number, number] => [(x - 0.07) * k, (y - 0.42) * k]);
  slab(s, scaled, 0.07, p.color('bolt.1'), {
    bevel: 0.02, bevelColor: p.color('bolt.2'), sideColor: p.color('bolt.0'),
    tris: [0, 1, 2, 0, 2, 5, 0, 5, 6, 2, 3, 4, 2, 4, 5],
  });
  s.flush(mb, rotX(0, 0.36, 0.39, -0.42));
  mb.blob(0.05, 0.12, 0.03, p.color('puWhite.2'), place(-0.2, 0.6, 0.24), { detail: 1 });
};

/** A crystal ball in `ramp` on a gold stand, claws round its foot. */
export const crystalBall = (ramp: string): Builder => (mb, p) => {
  const s = new Smooth();
  const stand: Array<[number, number]> = [
    [0, 0], [0.38, 0], [0.4, 0.06], [0.34, 0.12], [0.2, 0.16], [0.17, 0.22], [0.27, 0.3], [0.31, 0.36], [0, 0.36],
  ];
  lathe(s, stand, 20, (seg) => p.color(seg === 1 || seg === 6 ? 'puGold.2' : 'puGold.1'));
  s.flush(mb);
  mb.blob(0.44, 0.44, 0.44, p.color(`${ramp}.1`), place(0, 0.72, 0), { topColor: p.color(`${ramp}.2`), detail: 3 });
  for (let i = 0; i < 4; i++) {
    const a = Math.PI / 4 + (i / 4) * Math.PI * 2;
    mb.blob(0.07, 0.13, 0.07, p.color('puGold.1'), place(Math.sin(a) * 0.3, 0.4, Math.cos(a) * 0.3), { detail: 1 });
  }
  // A swirl of light inside and the window glint.
  mb.blob(0.2, 0.06, 0.04, p.color(`${ramp}.2`), rotZ(0.08, 0.56, 0.3, 0.42), { detail: 1 });
  mb.blob(0.07, 0.11, 0.04, p.color('puWhite.2'), place(-0.2, 0.92, 0.33), { detail: 1 });
  mb.blob(0.04, 0.04, 0.03, p.color('puWhite.2'), place(-0.08, 1.0, 0.36), { detail: 1 });
};

/** A gold hand bell: a flared lathe with a bright lip and bead, the clapper under it, a loop on top. */
export const bell: Builder = (mb, p) => {
  const s = new Smooth();
  const profile: Array<[number, number]> = [
    [0, 0.16], [0.48, 0.13], [0.56, 0.17], [0.54, 0.23], [0.45, 0.31], [0.37, 0.43],
    [0.33, 0.57], [0.31, 0.72], [0.29, 0.85], [0.23, 0.96], [0.13, 1.02], [0, 1.04],
  ];
  lathe(s, profile, 28, (seg) => p.color(seg >= 1 && seg <= 2 ? 'puGold.2' : 'puGold.1'));
  s.flush(mb);
  tube(s, circlePath(0, 0, 0.42, 28), 0.035, 8, p.color('puGold.2'), { closed: true });
  s.flush(mb, rotX(0, 0.34, 0, Math.PI / 2));
  mb.blob(0.12, 0.12, 0.12, p.color('puGold.0'), place(0, 0.1, 0), { detail: 2 });
  tube(s, circlePath(0, 1.13, 0.11, 16), 0.045, 8, p.color('puGold.1'), { closed: true });
  s.flush(mb);
  mb.blob(0.06, 0.13, 0.03, p.color('puWhite.2'), place(-0.17, 0.66, 0.26), { detail: 1 });
};
